import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import { CellWithChildren, DateTimeCell, MaxLengthCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { Icon } from '@components-clean/core/icons/icon';
import { DatasetDetails } from '@interfaces/central-monitoring';
import { convertDateTimeToMoment } from '@utils-clean/date-time-helpers';
import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';

interface IRow extends DatasetDetails {
    transformedAutomationDetails: {
        status: string;
        trigger: string;
        excludedSitesCsv: string;
        excludedPanelsCsv: string;
        reviewGeneratedOn: string;
    };
}

export const siteAutomationColumns = (t: TFunction): GridColumnProps[] => {
    const { TooltipCell } = useGridCustomCell();
    return [
        {
            field: 'isActive',
            title: t('grid.columns.isActive'),
            locked: true,
            filter: 'boolean',
            cells: {
                data: (props) => {
                    const row = props.dataItem;
                    return (
                        <CellWithChildren {...props}>
                            <FontAwesomeIcon icon={['far', row.isActive ? 'eye' : 'eye-slash']} />
                        </CellWithChildren>
                    );
                },
            },
            minWidth: 70,
        },
        {
            field: 'date',
            title: t('grid.columns.date'),
            locked: true,
            filter: 'date',
            cellName: 'DateCell',
            cells: {
                data: (props) => {
                    const row = props.dataItem as IRow;
                    return (
                        <CellWithChildren {...props}>
                            <span
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <div className="ellipsis-overflow">
                                    {convertDateTimeToMoment(new Date(row.date), 'DD-MMM-yyyy')}
                                </div>
                                {row.isMigrated && (
                                    <Icon
                                        dataTestId="migrated-icon"
                                        title={`Migrated on ${stringDateWithoutTimeFromUtcNonNullable(
                                            row.migratedOn,
                                            'DD-MMM-yyyy',
                                        )}`}
                                        icon={['far', 'file-import']}
                                        tooltipPosition="top"
                                    />
                                )}
                            </span>
                        </CellWithChildren>
                    );
                },
            },
            minWidth: 100,
        },
        {
            field: 'transformedAutomationDetails.status',
            title: t('grid.columns.automationStatus'),
            locked: true,
            minWidth: 150,
        },
        {
            field: 'transformedAutomationDetails.siteTrigger',
            title: t('grid.columns.automationTrigger'),
            minWidth: 125,
        },
        {
            field: 'transformedAutomationDetails.excludedSitesCsv',
            title: t('grid.columns.automationExcludedSites'),
            cells: { data: TooltipCell({ isCsv: true }) },
            minWidth: 330,
        },
        {
            field: 'transformedAutomationDetails.excludedPanelsCsv',
            title: t('grid.columns.automationExcludedPanels'),
            cells: { data: TooltipCell({ isCsv: true }) },
            minWidth: 580,
        },
        {
            field: 'transformedAutomationDetails.reviewGeneratedOn',
            title: t('grid.columns.automationReviewGeneratedOn'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 110,
            cellName: 'DateCell',
        },
        {
            field: 'automation.reviewGeneratedByName',
            title: t('grid.columns.automationReviewGeneratedBy'),
            minWidth: 150,
        },
        {
            field: 'automation.reviewRevertedOn',
            title: t('grid.columns.automationReviewRevertedOn'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 110,
            cellName: 'DateCell',
        },
        {
            field: 'automation.reviewRevertedByName',
            title: t('grid.columns.automationReviewRevertedBy'),
            minWidth: 150,
        },
        {
            field: 'automation.reviewGenerationRevertReason',
            title: t('grid.columns.automationReviewGenerationRevertReason'),
            cells: { data: MaxLengthCell(50) },
            minWidth: 300,
        },
    ];
};

export const controlsAutomationColumns = (t: TFunction): GridColumnProps[] => {
    const { TooltipCell } = useGridCustomCell();
    return [
        {
            field: 'isActive',
            title: t('grid.columns.isActive'),
            locked: true,
            filter: 'boolean',
            cells: {
                data: (props) => {
                    const row = props.dataItem;
                    return (
                        <CellWithChildren {...props}>
                            <FontAwesomeIcon icon={['far', row.isActive ? 'eye' : 'eye-slash']} />
                        </CellWithChildren>
                    );
                },
            },
            minWidth: 60,
        },
        {
            field: 'date',
            title: t('grid.columns.date'),
            locked: true,
            filter: 'date',
            cellName: 'DateCell',
            cells: {
                data: (props) => {
                    const row = props.dataItem as IRow;
                    return (
                        <CellWithChildren {...props}>
                            <span
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <div className="ellipsis-overflow">
                                    {convertDateTimeToMoment(new Date(row.date), 'DD-MMM-yyyy')}
                                </div>
                                {row.isMigrated && (
                                    <Icon
                                        dataTestId="migrated-icon"
                                        title={`Migrated on ${stringDateWithoutTimeFromUtcNonNullable(
                                            row.migratedOn,
                                            'DD-MMM-yyyy',
                                        )}`}
                                        icon={['far', 'file-import']}
                                        tooltipPosition="top"
                                    />
                                )}
                            </span>
                        </CellWithChildren>
                    );
                },
            },
            minWidth: 100,
        },
        {
            field: 'transformedAutomationDetails.status',
            title: t('grid.columns.automationStatus'),
            locked: true,
            minWidth: 150,
        },
        {
            field: 'transformedAutomationDetails.controlsTrigger',
            title: t('grid.columns.automationTrigger'),
            cells: { data: TooltipCell({ isCsv: true }) },
            minWidth: 125,
        },
        {
            field: 'transformedAutomationDetails.excludedPanelsCsv',
            title: t('grid.columns.automationExcludedPanels'),
            cells: { data: TooltipCell({ isCsv: true }) },
            minWidth: 400,
        },
        {
            field: 'transformedAutomationDetails.reviewGeneratedOn',
            title: t('grid.columns.automationReviewGeneratedOn'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 110,
            cellName: 'DateCell',
        },
        {
            field: 'automation.reviewGeneratedByName',
            title: t('grid.columns.automationReviewGeneratedBy'),
            minWidth: 150,
        },
        {
            field: 'automation.reviewRevertedOn',
            title: t('grid.columns.automationReviewRevertedOn'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 110,
            cellName: 'DateCell',
        },
        {
            field: 'automation.reviewRevertedByName',
            title: t('grid.columns.automationReviewRevertedBy'),
            minWidth: 150,
        },
        {
            field: 'automation.reviewGenerationRevertReason',
            title: t('grid.columns.automationReviewGenerationRevertReason'),
            cells: { data: MaxLengthCell(50) },
            minWidth: 300,
        },
    ];
};
